import React, { useEffect } from 'react';

import type { OptionsFromRequest } from '../../../../core/system/rendering/types.js';
import type { ByfunnFrapiTagCollectionPage } from '../../../types/frapi-types.js';

import Navigation from '../../components/commonComponents/Navigation/Navigation.js';
import Footer from '../../components/commonComponents/Footer.js';
import { trackTagPageView, withByfunnPulse } from '../../../pulse/index.js';
import { SearchBar } from '../../components/search/SearchBar.js';
import { usePulseContext } from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { NoResults } from '../../components/search/NoResults.js';
import { EmptySearch } from '../../components/search/EmptySearch.js';
import { sectionMappings } from '../front/sectionHelpers.js';
import TeaserSearchResult from '../../components/teasers/TeaserSearchResult.js';
import { useActiveSectionSearch } from '../../hooks/useActiveSectionSearch.js';
import { Sorter } from '../../components/search/Sorter.js';
import { PercentageInfo } from '../../components/search/PercentageInfo.js';
import { useManageSearch } from '../../hooks/useManageSearch.js';
import { getSorter } from '../../components/search/helpers.js';

export type TagCollectionPageProps = {
  data: ByfunnFrapiTagCollectionPage;
  options: OptionsFromRequest;
};

const TagCollectionPage: React.FC<TagCollectionPageProps> = ({
  data,
  options,
}) => {
  const { siteVersion, currentUrl } = options;
  const { articles } = data;
  const isMobile = siteVersion === 'phone';

  const { section, ids } = options.requestParams;
  const query = `/samling/${currentUrl.split('/').pop()}`;
  const sorter = getSorter(currentUrl);

  const parsedIds = ids.replace(/&tag=\|?/g, '');

  const { activeSection, setActiveSection } = useActiveSectionSearch(section);

  const { collection, handleSort, handleNext, sortConfig } = useManageSearch(
    articles.items,
    sorter || null,
    query,
  );

  const { trackViewListing, registerViewElementTracking } = usePulseContext();
  const { trackClickUIElement } = usePulseContext();
  const handleOnFetchMoreClick = () => {
    trackClickUIElement({
      object: {
        id: 'tag-collection-result:fetch-more',
        name: 'Fetch more tag collection teasers',
        elementType: 'Button',
      },
    }).finally(() => handleNext());
  };

  useEffect(() => {
    trackTagPageView(trackViewListing, currentUrl, 'tag collection');

    registerViewElementTracking({
      object: {
        page: {
          id: 'tag-collection',
          type: 'Listing',
        },
      },
    });
  }, [trackViewListing, registerViewElementTracking, currentUrl, data]);

  const activeFilters = data.info;
  const isActiveSection = section === activeSection;
  const numberOfHits = data.articles.hits.total;

  return (
    <div className={`${siteVersion}-front`}>
      <Navigation fullWidth={!isMobile} />
      <div className="tag-page-container">
        {isActiveSection ? (
          <>
            <div className="tag-page-top">
              <h1 className="serp-title">{`${
                sectionMappings[activeSection]?.searchTitle || 'Vink'
              }`}</h1>
              <SearchBar
                isMobile={isMobile}
                presetActiveFilters={activeFilters}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                filterString={parsedIds}
              />
            </div>
            <div className="search-results-controls">
              <div className="search-results-hits">{`${numberOfHits} treff`}</div>
              <Sorter
                handleSort={handleSort}
                activeSort={sortConfig}
                section={activeSection}
              />
            </div>
            {collection.length > 0 ? (
              <>
                <div className="tag-articles-all-wrapper">
                  <div className="tag-articles-all">
                    {collection.map((t, i) => (
                      <TeaserSearchResult
                        {...t}
                        key={t.id}
                        index={i}
                        size="small"
                        mobile={isMobile}
                        fullWidthTeaser={false}
                        lazyLoad={false}
                        colorPalette={undefined}
                      />
                    ))}
                  </div>
                </div>
                <div className="percentage-pagination">
                  <PercentageInfo
                    resultsCount={numberOfHits}
                    totalSize={collection.length}
                  />
                  {collection.length < numberOfHits && (
                    <button
                      className="fetch-more"
                      onClick={handleOnFetchMoreClick}
                    >{`Vis flere saker`}</button>
                  )}
                </div>
              </>
            ) : (
              <NoResults section={section} />
            )}
          </>
        ) : (
          <EmptySearch
            isMobile={isMobile}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
          />
        )}
      </div>
      <Footer isMobile={isMobile} />
    </div>
  );
};

const TagCollectionPageWithPulse = withByfunnPulse(TagCollectionPage);

export { TagCollectionPageWithPulse as TagCollectionPage };
